// PreventiApp · Sprint 3 · Etapa 6
// Data layer Supabase para observaciones móviles + cola offline-first.
//
// Reglas:
//  - Cada observación lleva un client_id UUID generado en el dispositivo.
//  - INSERT idempotente: la BD tiene UNIQUE(autor_id, client_id) → re-intentos no duplican.
//  - Estado local de sync: 'pendiente' | 'sincronizando' | 'sincronizado' | 'fallido'.
//  - Storage: subida de fotos al bucket privado 'evidencias' bajo
//      `${colegio_id}/${observacion_id}/foto_${idx}.${ext}`.
//  - El service key NUNCA se usa aquí. Sólo la clave publicable + sesión del usuario.
//  - El service worker / network reconnection dispara `processQueue()`.
//
// La cola vive en localStorage (clave QUEUE_KEY) — se limpia automáticamente
// cuando la observación se sincroniza con éxito.

import { supabase } from '../lib/supabase';

const QUEUE_KEY = 'preventiapp_offline_queue_v1';
// Namespaces legacy de Sprint 2 (a borrar al primer login):
export const LEGACY_KEYS = [
  'preventiapp_mobile_obs_operativo_v1',
  'preventiapp_mobile_obs_induccion_v1',
];

// Mapas locales → códigos de BD ----------------------------------------------
// Los IDs del observationEngine ya son D1..D6, así que la mappping es directa.
const dominioCode = (dominio) => dominio?.id || null;

// UUID v4 mínima dependencia-cero (crypto.randomUUID donde existe).
export function newUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback RFC4122 v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Limpia datos legacy de Sprint 2 (mock localStorage). Se invoca al primer login.
export function purgeLegacyData() {
  let removed = 0;
  for (const k of LEGACY_KEYS) {
    if (localStorage.getItem(k) !== null) {
      localStorage.removeItem(k);
      removed += 1;
    }
  }
  // También removemos draft anterior si existiera con shape obsoleto
  return removed;
}

// ----------------------------- Cola offline ----------------------------------
function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
}
function writeQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* full disk */ }
}

export function enqueueObservation(payload) {
  const q = readQueue();
  // Si ya hay item con mismo client_id, refrescar payload (idempotente)
  const idx = q.findIndex((i) => i.payload.client_id === payload.client_id);
  const item = {
    queued_at: new Date().toISOString(),
    estado_sync: 'pendiente',
    retries: 0,
    payload,
  };
  if (idx >= 0) q[idx] = { ...q[idx], ...item };
  else q.push(item);
  writeQueue(q);
  return item;
}

export function getQueueStats() {
  const q = readQueue();
  return {
    total: q.length,
    pendientes: q.filter((i) => i.estado_sync !== 'sincronizado').length,
  };
}

// ----------------------------- Payload builder -------------------------------
// Transforma el draft de la app móvil al schema de public.observaciones.
export function buildObservationPayload({ draft, mode, modoRegistro, profile, clientId }) {
  return {
    client_id: clientId || newUUID(),
    colegio_id: profile.colegio_id,
    autor_id: profile.id,
    dominio_code: dominioCode(draft.dominio),
    conducta_code: draft.conducta?.code || null,
    curso_texto: draft.curso || null, // texto libre (curso_id queda NULL)
    estudiante: draft.estudiante || null,
    contexto: draft.contexto || null,
    frecuencia: draft.frecuencia || null,
    intensidad: draft.intensidad || null,
    duracion: draft.duracion || null,
    participantes: draft.participantes || null,
    fuente: draft.fuente || null,
    modo_registro: modoRegistro,           // 'operativo' | 'induccion'
    flujo_captura: mode === 'rapido' ? 'rapido' : 'completo',
  };
}

// ----------------------------- INSERT idempotente ----------------------------
export async function insertObservation(payload) {
  const { data, error } = await supabase
    .from('observaciones')
    .insert(payload)
    .select('id, client_id')
    .single();
  if (error) {
    // 23505 = unique_violation → ya existía (reintento offline). Recuperamos id por client_id.
    if (error.code === '23505') {
      const { data: existing } = await supabase
        .from('observaciones')
        .select('id, client_id')
        .eq('autor_id', payload.autor_id)
        .eq('client_id', payload.client_id)
        .maybeSingle();
      if (existing) return { id: existing.id, client_id: existing.client_id, deduped: true };
    }
    throw error;
  }
  return data;
}

// ----------------------------- Storage: subir fotos --------------------------
// Sprint 4: El pipeline oficial de fotos pasa OBLIGATORIAMENTE por anonimización
// (MediaPipe cliente + OpenCV servidor). Ver /api/evidencia/anonimizar y /confirmar.
// NO existe una ruta directa desde el frontend a supabase.storage — la imagen
// original NUNCA se persiste. El backend inserta en `observaciones_evidencias`
// dentro de /api/evidencia/confirmar. Ver `stagedFotos` en processQueue().

export async function insertEvidenciaTexto({ observacionId, contenido }) {
  if (!contenido?.trim()) return;
  const { error } = await supabase.from('observaciones_evidencias').insert({
    observacion_id: observacionId,
    tipo: 'texto',
    contenido_texto: contenido.trim(),
  });
  if (error) throw error;
}

// ---- Anonimización + confirmación (Sprint 4 doble capa) ----
// La imagen ORIGINAL nunca viaja: sólo el blob pixelado por MediaPipe en cliente.
// Backend re-valida con OpenCV, re-pixela si detecta rostros restantes, y guarda staging.
// `staged` es la entry de draft.evidencia.stagedFotos[]:
//   - Si online al capturar: { staging_path, preview_url, rostros_servidor, ... }
//   - Si offline al capturar: { pending_server_verification: true, pending_data_url }
async function anonimizarPendiente({ dataUrl, modoRegistro, token }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const blob = dataURLtoBlob(dataUrl);
  const fd = new FormData();
  fd.append('file', blob, 'evidencia.jpg');
  fd.append('obs_id', 'pending');
  fd.append('modo_registro', modoRegistro || 'operativo');
  fd.append('rostros_cliente', '0');
  const r = await fetch(`${API}/api/evidencia/anonimizar`, {
    method: 'POST',
    body: fd,
    headers: { Authorization: `Bearer ${token}` },
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.detail || `anonimizar HTTP ${r.status}`);
  return j; // { staging_path, preview_url, ... }
}

async function confirmarEvidencia({ obsId, stagingPath, token }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const r = await fetch(`${API}/api/evidencia/confirmar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ obs_id: obsId, staging_path: stagingPath }),
  });
  const j = await r.json();
  if (!r.ok) throw new Error(j.detail || `confirmar HTTP ${r.status}`);
  return j; // { evidencia_id, storage_path }
}

function dataURLtoBlob(dataUrl) {
  const arr = dataUrl.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const bstr = atob(arr[1]);
  const u8 = new Uint8Array(bstr.length);
  for (let i = 0; i < bstr.length; i += 1) u8[i] = bstr.charCodeAt(i);
  return new Blob([u8], { type: mime });
}

// ----------------------------- Sync principal --------------------------------
// Llamar en (a) save de cada observación; (b) al volver online; (c) al mount.
// Procesa la cola completa, marca items como 'sincronizado' o 'fallido'.
export async function processQueue() {
  if (!navigator.onLine) return { processed: 0, online: false };
  let q = readQueue();
  let processed = 0;
  let failed = 0;
  // Token para llamadas al backend de anonimización (validado por RLS via Supabase Auth).
  const token = (await supabase.auth.getSession()).data?.session?.access_token || null;
  for (let i = 0; i < q.length; i += 1) {
    const item = q[i];
    if (item.estado_sync === 'sincronizado') continue;
    try {
      item.estado_sync = 'sincronizando';
      writeQueue(q);
      const inserted = await insertObservation(item.payload);
      // Subir evidencias fotos: pasan OBLIGATORIAMENTE por el pipeline de anonimización
      // (client-side pixelation + server-side OpenCV verification). NUNCA se sube la original.
      const evid = item.evidencia || {};
      const stagedFotos = Array.isArray(evid.stagedFotos) ? evid.stagedFotos : [];
      for (const staged of stagedFotos) {
        let stagingPath = staged.staging_path;
        // Caso offline al capturar: sólo tenemos el dataURL pixelado en cliente → validar ahora.
        if (!stagingPath && staged.pending_data_url) {
          if (!token) throw new Error('No auth token available for backend anonymization');
          const anon = await anonimizarPendiente({
            dataUrl: staged.pending_data_url,
            modoRegistro: item.payload.modo_registro,
            token,
          });
          stagingPath = anon.staging_path;
        }
        if (!stagingPath) throw new Error('stagedFoto sin staging_path ni pending_data_url');
        if (!token) throw new Error('No auth token available for backend confirmation');
        await confirmarEvidencia({ obsId: inserted.id, stagingPath, token });
      }
      if (evid.nota?.trim()) {
        await insertEvidenciaTexto({ observacionId: inserted.id, contenido: evid.nota });
      }
      item.estado_sync = 'sincronizado';
      item.observacion_id = inserted.id;
      processed += 1;
    } catch (e) {
      item.retries = (item.retries || 0) + 1;
      item.estado_sync = item.retries >= 5 ? 'fallido' : 'pendiente';
      item.last_error = String(e?.message || e).slice(0, 240);
      failed += 1;
    }
  }
  // Quitar los sincronizados (no necesitamos seguir cargándolos en cola)
  q = q.filter((i) => i.estado_sync !== 'sincronizado');
  writeQueue(q);
  return { processed, failed, online: true };
}

// ----------------------------- Lectura: Mis Registros ------------------------
// RLS filtra por autor_id (la policy obs_select_propias). Devolvemos las
// observaciones del usuario actual en el modo activo (operativo o inducción).
export async function listMisObservaciones(modoRegistro) {
  const { data, error } = await supabase
    .from('observaciones')
    .select(`
      id, client_id, dominio_code, conducta_code, curso_texto, estudiante,
      contexto, frecuencia, intensidad, duracion, participantes, fuente,
      flujo_captura, modo_registro, estado, creado_en, actualizado_en,
      observaciones_evidencias ( id, tipo, storage_path, contenido_texto, mime, segundos )
    `)
    .eq('modo_registro', modoRegistro)
    .order('creado_en', { ascending: false })
    .limit(200);
  if (error) throw error;
  return data || [];
}

// ----------------------------- Edición versionada ----------------------------
// El trigger DB tg_obs_version_on_update() crea la versión automáticamente al UPDATE.
export async function updateObservacion(id, changes) {
  // Sólo pasamos los campos editables permitidos por la policy obs_update_autor
  const allowed = ['estudiante', 'contexto', 'frecuencia', 'intensidad', 'duracion', 'participantes', 'fuente', 'estado'];
  const patch = {};
  for (const k of allowed) if (k in changes) patch[k] = changes[k];
  if (changes.estado === 'editado' || (changes.estudiante !== undefined || changes.contexto !== undefined)) {
    patch.estado = changes.estado || 'editado';
  }
  const { error } = await supabase
    .from('observaciones')
    .update(patch)
    .eq('id', id);
  if (error) throw error;
  return { id, ...patch };
}

export async function marcarErroneo(id) {
  const { error } = await supabase
    .from('observaciones')
    .update({ estado: 'marcado_erroneo' })
    .eq('id', id);
  if (error) throw error;
  return { id, estado: 'marcado_erroneo' };
}

export async function listVersiones(observacionId) {
  const { data, error } = await supabase
    .from('observaciones_versions')
    .select('id, accion, creado_en, actor_id, snapshot')
    .eq('observacion_id', observacionId)
    .order('creado_en', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ----------------------------- Catálogos -------------------------------------
export async function fetchCatalogos() {
  const [{ data: dominios }, { data: conductas }] = await Promise.all([
    supabase.from('cat_dominios').select('code, nombre, orden').order('orden'),
    supabase.from('cat_conductas').select('code, dominio_code, label, is_sentinel, orden').order('orden'),
  ]);
  return { dominios: dominios || [], conductas: conductas || [] };
}
